import styled, { css } from 'styled-components';
import { StyledSectionContainer, StyledButton } from '../../../../globalStyled';
import { fontDisplay, fontUi } from '../../../../theme/typography';

export const StyledPage = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: flex-start;
  width: 100%;
  flex-wrap: wrap;
  gap: 16px;
`;

export const StyledColumn = styled.div`
  width: 100%;
  max-width: 560px;
  flex: 1 1 420px;
  @media only screen and (max-width: 1199px) {
    max-width: none;
  }
`;

export const StyledSection = styled(StyledSectionContainer)`
  overflow: hidden;

  h3 {
    ${fontDisplay};
    width: 100%;
    font-size: 20px;
    line-height: 1.15;
    letter-spacing: -0.02em;
    border-bottom: 1px solid ${({ theme }) => theme.borderSubtle};
    padding: 17px 22px;
    margin: 0;
    color: ${({ theme }) => theme.text};
  }
`;

export const StyledBody = styled.div`
  padding: 16px 22px 20px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const StyledOverview = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 4px;

  p {
    ${fontUi};
    font-size: 13px;
    line-height: 1.4;
    color: ${({ theme }) => theme.textMuted};
    margin: 0;
  }
`;

export const StyledActionButton = styled(StyledButton)`
  min-width: 0;
  width: auto;
  height: 32px;
  margin: 0;
  padding: 0 12px;
  font-size: 13px;
  font-weight: 600;
  box-shadow: none;
  flex-shrink: 0;
  white-space: nowrap;
  && {
    ${({ theme }) =>
      theme.isDark
        ? css`
            background: transparent;
            color: ${theme.text};
            border: 1px solid ${theme.border};
            &:hover:not(:disabled) {
              background: ${theme.hover};
            }
          `
        : css`
            background: ${theme.button};
            color: ${theme.buttonText};
            border: 1px solid ${theme.button};
          `}
  }
`;

export const StyledSubTitle = styled.h4`
  ${fontUi};
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  margin: 14px 0 8px;
  color: ${({ theme }) => theme.textFaint};
`;

export const StyledMutedNote = styled.p`
  ${fontUi};
  margin: 0 0 8px;
  font-size: 12px;
  line-height: 1.4;
  color: ${({ theme }) => theme.textFaint};
`;

export const StyledList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  border: 1px solid ${({ theme }) => theme.borderSubtle};
  border-radius: 8px;
  overflow: hidden;
  background: ${({ theme }) =>
    theme.isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.015)'};
`;

export const StyledListItem = styled.li`
  ${fontUi};
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  padding: ${({ $flush }) => ($flush ? 0 : '10px 12px')};
  font-size: 13px;
  color: ${({ theme }) => theme.text};
  border-bottom: 1px solid ${({ theme }) => theme.borderSubtle};

  &:last-child {
    border-bottom: 0;
  }
`;

export const StyledPastMatchButton = styled.button`
  ${fontUi};
  appearance: none;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  width: 100%;
  margin: 0;
  padding: 10px 12px;
  font-size: 13px;
  color: ${({ theme }) => theme.text};
  text-align: left;
  background: transparent;
  border: 0;
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.hover || 'rgba(0,0,0,0.04)'};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.headerAccent};
    outline-offset: -2px;
  }
`;

export const StyledClubCell = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  flex: 1;
`;

export const StyledCrest = styled.img`
  width: 28px;
  height: 28px;
  object-fit: contain;
  flex-shrink: 0;
  border-radius: 4px;
`;

export const StyledCrestFallback = styled.span`
  ${fontUi};
  width: 28px;
  height: 28px;
  flex-shrink: 0;
  border-radius: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  color: ${({ theme }) => theme.textMuted};
  background: ${({ theme }) => theme.hover || 'rgba(0,0,0,0.06)'};
  border: 1px solid ${({ theme }) => theme.borderSubtle};
`;

export const StyledClubName = styled.span`
  ${fontUi};
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.text};
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const StyledMatchup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex: 1;
`;

export const StyledMatchSide = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  flex: 1;

  &:last-of-type {
    flex-direction: row-reverse;
    text-align: right;
  }
`;

export const StyledMatchName = styled.span`
  ${fontUi};
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.text};
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const StyledMatchScore = styled.span`
  ${fontDisplay};
  flex-shrink: 0;
  min-width: 42px;
  text-align: center;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: -0.02em;
  color: ${({ theme }) => theme.text};
`;

export const StyledMatchDone = styled.span`
  ${fontUi};
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.textFaint};
`;

export const StyledOfferRow = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
`;

export const StyledOfferMeta = styled.div`
  ${fontUi};
  margin-top: 3px;
  font-size: 11px;
  line-height: 1.35;
  color: ${({ theme }) => theme.textFaint};
`;

export const StyledOfferActions = styled.div`
  display: flex;
  flex-direction: row;
  gap: 6px;
  flex-shrink: 0;
`;

export const StyledOfferButton = styled.button`
  ${fontUi};
  font-size: 12px;
  font-weight: 600;
  padding: 6px 10px;
  border-radius: 6px;
  cursor: pointer;
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ $primary, theme }) => ($primary ? theme.button : 'transparent')};
  color: ${({ $primary, theme }) => ($primary ? theme.buttonText : theme.text)};
  &:disabled {
    opacity: 0.45;
    cursor: default;
  }
  &:not(:disabled):hover {
    filter: brightness(0.97);
  }
`;

export const StyledGoalRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  border-bottom: 1px solid ${({ theme }) => theme.borderSubtle};

  &:last-child {
    border-bottom: 0;
  }
`;

export const StyledGoalLabel = styled.span`
  ${fontUi};
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.text};
`;

export const StyledGoalValue = styled.span`
  ${fontUi};
  font-size: 13px;
  font-weight: 700;
  color: ${({ theme }) => theme.textMuted};
`;

export const StyledEmptyRow = styled.div`
  ${fontUi};
  padding: 12px;
  font-size: 13px;
  color: ${({ theme }) => theme.textFaint};
`;
